// Tab "Hồ sơ 360": chỉ số chính, việc cần làm, phân loại tóm tắt, hành trình khách hàng, lịch hẹn
import React, { useState } from 'react';
import { Timeline } from 'antd';
import dayjs from 'dayjs';
import {
  AlertTriangle, CalendarPlus, Camera, CircleDollarSign, FileSignature, HeartHandshake, PenLine, RefreshCw, TrendingUp,
} from 'lucide-react';
import { Kpi, money, moneyShort } from '../Treatment/ui';
import { SEGMENT, type ActionKind, type JourneyItem } from './logic';
import { Btn, LinkBar, Section, SegmentBadge, TierBadge, type C360Ops } from './ui360';
import type { C360 } from './useCustomer360';

const ACTION_ICON: Record<ActionKind, React.ReactNode> = {
  pay: <CircleDollarSign className="h-4 w-4" />,
  book: <CalendarPlus className="h-4 w-4" />,
  sign: <FileSignature className="h-4 w-4" />,
  visit: <PenLine className="h-4 w-4" />,
  photo: <Camera className="h-4 w-4" />,
  renew: <RefreshCw className="h-4 w-4" />,
  care: <HeartHandshake className="h-4 w-4" />,
  tier: <TrendingUp className="h-4 w-4" />,
};
const TONE = {
  danger: 'border-red-200 bg-red-50 text-red-800',
  warn: 'border-amber-200 bg-amber-50 text-amber-900',
  info: 'border-slate-200 bg-white text-slate-700',
};
const JOURNEY_COLOR: Record<JourneyItem['kind'], string> = {
  profile: 'gray', order: 'blue', card: 'green', use: '#3e91ab', pay: 'green', sign: 'purple', photo: 'cyan', refund: 'red',
};

export default function Overview360({ c, ops, children }: { c: C360; ops: C360Ops; children?: React.ReactNode }) {
  const { metrics: m, cls } = c;
  const [allJourney, setAllJourney] = useState(false);
  const journey = allJourney ? c.journey : c.journey.slice(0, 8);

  const run = (a: C360['actions'][number]) => {
    if (a.kind === 'pay' && a.cardId) ops.pay(a.cardId);
    else if (a.kind === 'book' && a.cardId) ops.book(a.cardId);
    else if (a.kind === 'photo') ops.upload({ cardId: a.cardId });
    else if (a.kind === 'sign') ops.goTab('sign');
    else if (a.kind === 'visit' && a.visitDate) ops.signVisit(a.visitDate);
    else if (a.kind === 'renew' && a.cardId) ops.openCard(a.cardId);
    else if (a.kind === 'tier') ops.sell();
    else ops.goTab('class');
  };
  const actionLabel: Record<ActionKind, string> = { pay: 'Gửi POS thu', book: 'Đặt lịch', sign: 'Xem', visit: 'Ký xác nhận', photo: 'Tải ảnh', renew: 'Xem thẻ', care: 'Xem', tier: 'Bán thẻ' };

  return (
    <div className="space-y-4">
      <LinkBar c={c} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Tổng chi tiêu" value={`${moneyShort(m.lifetimeSpend)}`} sub={<>{c.cfg.windowMonths} tháng: <b>{moneyShort(m.spendWindow)}</b> · TB/đơn {moneyShort(m.avgTicket)}</>} onClick={() => ops.goTab('ledger')} />
        <Kpi label="Số lần đến" value={m.visits} onClick={() => ops.goTab('visits')} sub={m.lastVisit ? <>Lần cuối {dayjs(m.lastVisit).format('DD/MM/YYYY')} ({m.daysSinceLast} ngày){c.visits.some(v => v.status === 'pending' || v.status === 'partial') ? <span className="text-amber-700"> · {c.visits.filter(v => v.status === 'pending' || v.status === 'partial').length} chờ ký</span> : ''}</> : 'Chưa đến lần nào'} />
        <Kpi label="Thẻ đang dùng" value={m.activeCards} sub={<>Còn <b>{m.sessionsLeft}</b> buổi{m.valueLeft ? <> · {moneyShort(m.valueLeft)} trả trước</> : ''}</>} onClick={() => ops.goTab('treatment')} />
        <Kpi label="Công nợ" value={`${money(m.debt)}đ`} tone={m.overdue ? 'danger' : m.debt ? 'warn' : 'ok'} sub={m.overdue ? <span className="text-red-600">Quá hạn {money(m.overdue)}đ</span> : m.debt ? 'Chưa đến hạn' : 'Không nợ'} onClick={() => ops.goTab('treatment')} />
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <Section className="lg:col-span-3" title={<span className="flex items-center gap-2">Việc cần làm {c.actions.length > 0 && <span className="rounded-full bg-[#357f96] px-2 text-xs text-white">{c.actions.length}</span>}</span>}>
          {c.actions.length ? (
            <div className="space-y-2">
              {c.actions.map(a => (
                <div key={a.key} className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${TONE[a.tone]}`}>
                  <span className="shrink-0">{a.tone === 'danger' ? <AlertTriangle className="h-4 w-4" /> : ACTION_ICON[a.kind]}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">{a.title}</div>
                    <div className="truncate text-xs opacity-80">{a.detail}</div>
                  </div>
                  <Btn onClick={() => run(a)}>{actionLabel[a.kind]}</Btn>
                </div>
              ))}
            </div>
          ) : <div className="py-6 text-center text-sm text-slate-400">Không có việc tồn đọng</div>}
        </Section>

        <Section className="lg:col-span-2" title="Phân loại" extra={<Btn onClick={() => ops.goTab('class')}>Chi tiết</Btn>}>
          <div className="flex flex-wrap items-center gap-2">
            <TierBadge tier={cls.tier} manual={!!cls.override?.tier} size="lg" />
            <SegmentBadge segment={cls.segment} manual={!!cls.override?.segment} />
          </div>
          <p className="mt-2 text-xs text-slate-500">{SEGMENT[cls.segment].advice}</p>
          {cls.next ? (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-slate-500"><span>Lên hạng {cls.next.tier}</span><span>còn {moneyShort(cls.next.need)}</span></div>
              <div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-[#3e91ab]" style={{ width: `${Math.round(cls.next.pct * 100)}%` }} /></div>
            </div>
          ) : <div className="mt-3 text-xs font-semibold text-indigo-700">Đã đạt hạng cao nhất</div>}
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            {([['R', 'Gần đây', cls.rfm.r], ['F', 'Tần suất', cls.rfm.f], ['M', 'Chi tiêu', cls.rfm.m]] as const).map(([k, label, v]) => (
              <div key={k} className="rounded-lg bg-slate-50 py-2">
                <div className="text-lg font-extrabold text-slate-800">{v}<span className="text-xs font-normal text-slate-400">/5</span></div>
                <div className="text-[11px] text-slate-500">{label}</div>
              </div>
            ))}
          </div>
          {c.interests.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs">
              <span className="text-slate-500">Quan tâm:</span>
              {c.interests.slice(0, 4).map(x => <span key={x.group} className="rounded-full bg-[#ecf6f9] px-2 py-0.5 font-medium text-[#2c6b7f]">{x.group}</span>)}
            </div>
          )}
        </Section>
      </div>

      <Section title="Hành trình khách hàng" extra={c.journey.length > 8 && <Btn onClick={() => setAllJourney(x => !x)}>{allJourney ? 'Thu gọn' : `Xem tất cả (${c.journey.length})`}</Btn>}>
        {journey.length ? (
          <Timeline className="!mb-0 !mt-1" items={journey.map(j => ({
            color: JOURNEY_COLOR[j.kind],
            children: (
              <div className="text-sm">
                <span className="font-semibold text-slate-800">{j.title}</span>
                <span className="ml-2 text-xs text-slate-400">{dayjs(j.at).format(j.kind === 'photo' ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm')}</span>
                {j.detail && <div className="text-xs text-slate-500">{j.detail}</div>}
              </div>
            ),
          }))} />
        ) : <div className="py-4 text-center text-sm text-slate-400">Chưa có hoạt động</div>}
      </Section>

      {children}
    </div>
  );
}
